/**
 * Persistence.
 *
 * Offline-first: everything lives in IndexedDB on the device, keyed by table
 * id, so a dropped connection never interrupts a game. There is deliberately
 * no server sync here — add it later behind this same interface if the venue
 * wants cross-device visibility.
 *
 * The in-memory implementation exists so the game logic can be tested without
 * a browser.
 */

import type { Game } from './types';

export interface GameStore {
  save(game: Game): Promise<void>;
  load(gameId: string): Promise<Game | undefined>;
  /** All games for one table, newest first. */
  listByTable(tableId: string): Promise<Game[]>;
  /** Every game this device has ever saved, across every table, newest first — backs the "Past Games" screen. */
  listAll(): Promise<Game[]>;
  delete(gameId: string): Promise<void>;
}

// ---------------------------------------------------------------------------
// In-memory (tests, SSR, fallback)
// ---------------------------------------------------------------------------

export class MemoryGameStore implements GameStore {
  private games = new Map<string, Game>();

  async save(game: Game): Promise<void> {
    this.games.set(game.id, structuredClone(game));
  }

  async load(gameId: string): Promise<Game | undefined> {
    const g = this.games.get(gameId);
    return g ? structuredClone(g) : undefined;
  }

  async listByTable(tableId: string): Promise<Game[]> {
    return [...this.games.values()]
      .filter((g) => g.tableId === tableId)
      .sort((a, b) => b.startedAt - a.startedAt)
      .map((g) => structuredClone(g));
  }

  async listAll(): Promise<Game[]> {
    return [...this.games.values()]
      .sort((a, b) => b.startedAt - a.startedAt)
      .map((g) => structuredClone(g));
  }

  async delete(gameId: string): Promise<void> {
    this.games.delete(gameId);
  }
}

// ---------------------------------------------------------------------------
// IndexedDB
// ---------------------------------------------------------------------------

const DB_NAME = 'mahjong-scorer';
const DB_VERSION = 1;
const STORE = 'games';

export class IndexedDbGameStore implements GameStore {
  private dbPromise?: Promise<IDBDatabase>;

  private db(): Promise<IDBDatabase> {
    this.dbPromise ??= new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          const os = db.createObjectStore(STORE, { keyPath: 'id' });
          // Table id index: the mechanism that keeps concurrent tables apart.
          os.createIndex('tableId', 'tableId', { unique: false });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    return this.dbPromise;
  }

  private async tx<T>(
    mode: IDBTransactionMode,
    fn: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const db = await this.db();
    return new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      const req = fn(transaction.objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  async save(game: Game): Promise<void> {
    await this.tx('readwrite', (s) => s.put(game));
  }

  async load(gameId: string): Promise<Game | undefined> {
    return this.tx<Game | undefined>('readonly', (s) => s.get(gameId));
  }

  async listByTable(tableId: string): Promise<Game[]> {
    const games = await this.tx<Game[]>('readonly', (s) =>
      s.index('tableId').getAll(tableId),
    );
    return games.sort((a, b) => b.startedAt - a.startedAt);
  }

  async listAll(): Promise<Game[]> {
    const games = await this.tx<Game[]>('readonly', (s) => s.getAll());
    return games.sort((a, b) => b.startedAt - a.startedAt);
  }

  async delete(gameId: string): Promise<void> {
    await this.tx('readwrite', (s) => s.delete(gameId));
  }
}

/** Pick the right store for the current environment. */
export function createGameStore(): GameStore {
  return typeof indexedDB !== 'undefined'
    ? new IndexedDbGameStore()
    : new MemoryGameStore();
}
